/**
 * Reconnecting WebSocket client typed by the shared ws-protocol. The browser
 * connects to `/ws` (proxied to the API in dev) authenticated by the platform
 * cookie. One shared client carries both global messages (like `notify`) and
 * per-session builder traffic; call `subscribeSession` to join a session.
 */

import {
  CLIENT_MSG,
  makeEnvelope,
  parseApiMessage,
  type ApiToClientMessage,
  type ChatSendPayload,
  type SessionMode,
  type WsEnvelope,
} from '@proteam/shared';

export type WsStatus = 'connecting' | 'open' | 'closed';

type ApiMsgType = ApiToClientMessage['type'];
type ApiMsgOf<T extends ApiMsgType> = Extract<ApiToClientMessage, { type: T }>;
type ServerHandler<T extends ApiMsgType> = (msg: ApiMsgOf<T>) => void;

/** Derive the default WebSocket URL from the current origin (or an env override). */
export function defaultWsUrl(): string {
  if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL;
  if (typeof window === 'undefined') return 'ws://localhost:4301/ws';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

export class WsClient {
  private readonly url: string;
  private ws: WebSocket | null = null;
  private status: WsStatus = 'closed';
  private closedByUser = false;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly queue: string[] = [];

  private readonly handlers = new Map<string, Set<(msg: ApiToClientMessage) => void>>();
  private readonly openListeners = new Set<() => void>();
  private readonly statusListeners = new Set<(status: WsStatus) => void>();

  constructor(url: string = defaultWsUrl()) {
    this.url = url;
  }

  getStatus(): WsStatus {
    return this.status;
  }

  /** Open the connection. Safe to call repeatedly; a no-op when already live. */
  connect(): void {
    this.closedByUser = false;
    if (this.ws && (this.status === 'open' || this.status === 'connecting')) return;
    this.setStatus('connecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;

    socket.onopen = () => {
      this.attempts = 0;
      this.setStatus('open');
      for (const frame of this.queue.splice(0)) socket.send(frame);
      for (const cb of this.openListeners) cb();
    };
    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      let raw: unknown;
      try {
        raw = JSON.parse(event.data);
      } catch {
        return;
      }
      const msg = parseApiMessage(raw);
      if (!msg) return;
      const set = this.handlers.get(msg.type);
      if (set) for (const handler of set) handler(msg);
    };
    socket.onclose = () => {
      this.ws = null;
      this.setStatus('closed');
      if (!this.closedByUser) this.scheduleReconnect();
    };
    socket.onerror = () => {
      // The close handler runs next and drives reconnection.
      socket.close();
    };
  }

  /** Permanently close the connection (no reconnect). */
  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }

  /** Subscribe to a server message type. Returns an unsubscribe function. */
  on<T extends ApiMsgType>(type: T, handler: ServerHandler<T>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const wrapped = handler as (msg: ApiToClientMessage) => void;
    set.add(wrapped);
    return () => {
      set?.delete(wrapped);
    };
  }

  /** Run a callback every time the socket (re)opens. Returns an unsubscribe. */
  onOpen(cb: () => void): () => void {
    this.openListeners.add(cb);
    if (this.status === 'open') cb();
    return () => this.openListeners.delete(cb);
  }

  /** Observe connection status changes. Fires immediately with the current status. */
  onStatus(cb: (status: WsStatus) => void): () => void {
    this.statusListeners.add(cb);
    cb(this.status);
    return () => this.statusListeners.delete(cb);
  }

  /** Send an already-built envelope, queuing it until the socket is open. */
  send(envelope: WsEnvelope): void {
    const frame = JSON.stringify(envelope);
    if (this.ws && this.status === 'open') {
      this.ws.send(frame);
    } else {
      this.queue.push(frame);
      this.connect();
    }
  }

  /* ----- Typed client -> API helpers ----- */

  subscribeSession(sessionId: string, mode?: SessionMode): void {
    this.send(makeEnvelope(CLIENT_MSG.sessionSubscribe, mode ? { mode } : {}, sessionId));
  }
  watchSession(sessionId: string): void {
    this.send(makeEnvelope(CLIENT_MSG.sessionWatch, {}, sessionId));
  }
  sendChat(sessionId: string, payload: ChatSendPayload): void {
    this.send(makeEnvelope(CLIENT_MSG.chatSend, payload, sessionId));
  }
  setMode(sessionId: string, mode: SessionMode): void {
    this.send(makeEnvelope(CLIENT_MSG.modeSet, { mode }, sessionId));
  }
  setChatOnly(sessionId: string, on: boolean): void {
    this.send(makeEnvelope(CLIENT_MSG.chatOnlySet, { on }, sessionId));
  }
  checkpointDone(sessionId: string, checkpointId: string): void {
    this.send(makeEnvelope(CLIENT_MSG.checkpointDone, { checkpointId }, sessionId));
  }
  requestFix(sessionId: string, error: string): void {
    this.send(makeEnvelope(CLIENT_MSG.fixRequest, { error }, sessionId));
  }
  restoreTask(sessionId: string, taskLine: string): void {
    this.send(makeEnvelope(CLIENT_MSG.restoreTask, { taskLine }, sessionId));
  }
  /** Kick off a Big Build autonomous run for a session. */
  startRun(sessionId: string): void {
    this.send(makeEnvelope(CLIENT_MSG.runStart, { sessionId }, sessionId));
  }
  /** Pause an in-progress Big Build run. */
  pauseRun(sessionId: string): void {
    this.send(makeEnvelope(CLIENT_MSG.runPause, { sessionId }, sessionId));
  }

  private setStatus(status: WsStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const cb of this.statusListeners) cb(status);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.closedByUser) return;
    const base = Math.min(30_000, 500 * 2 ** this.attempts);
    const delay = base / 2 + Math.random() * (base / 2);
    this.attempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

let shared: WsClient | null = null;

/** The app-wide shared WebSocket client, created on first use. */
export function getWs(): WsClient {
  if (!shared) shared = new WsClient();
  return shared;
}
